import {
  WIDE_PAIR_HASH_EMPTY,
  WIDE_PAIR_HASH_TOMBSTONE,
  canonicalBodyPair,
  decodeWidePairDescriptorPublication,
  encodeWidePairDescriptorPublication,
  equalCanonicalBodyPair,
  hashCanonicalBodyPair,
  type CanonicalBodyPair,
} from './pairAbi';

const BYTES_PER_U32 = Uint32Array.BYTES_PER_ELEMENT;
const U32_MAX = 0xffff_ffff;

/** The largest descriptor count that leaves both hash sentinels unambiguous. */
export const WIDE_COMPACT_CONTACT_MAX_DESCRIPTOR_COUNT = 0xffff_fffe;
/** Default WebGPU dynamic/storage binding offset alignment. */
export const WIDE_COMPACT_CONTACT_DEFAULT_ALIGNMENT_BYTES = 256;

export interface WideCompactContactPoolLayoutInput {
  persistentManifoldHashCapacity: number;
  maxPersistentManifolds: number;
  pairManifoldSlots: number;
  compactContactPoolCapacity: number;
  /** Plane offsets are aligned so one allocation can expose independent bindings. */
  bindingOffsetAlignmentBytes?: number;
}

export interface WideCompactContactPoolRange {
  offset: number;
  length: number;
  end: number;
  byteOffset: number;
  byteLength: number;
}

/**
 * Feature-gated wide replacement for `CompactContactPoolLayout`.
 *
 * This is deliberately a separate type and planner. Nothing in the current
 * packed-u16 production path consumes it, so the legacy compact offsets remain
 * byte-for-byte unchanged. Hash slots contain only descriptorIndex + 1. Pair
 * identity lives exclusively in descriptorBodyA/descriptorBodyB.
 */
export interface WideCompactContactPoolLayout {
  encoding: 'exact-wide-u32';
  bindingOffsetAlignmentBytes: number;
  header: WideCompactContactPoolRange;
  hashPublication: WideCompactContactPoolRange;
  descriptorBodyA: WideCompactContactPoolRange;
  descriptorBodyB: WideCompactContactPoolRange;
  descriptorLastSeen: WideCompactContactPoolRange;
  descriptorResumeEpoch: WideCompactContactPoolRange;
  slotRow: WideCompactContactPoolRange;
  outputMask: WideCompactContactPoolRange;
  freeDescriptors: WideCompactContactPoolRange;
  freeRows0: WideCompactContactPoolRange;
  freeRows1: WideCompactContactPoolRange;
  ranges: readonly WideCompactContactPoolRange[];
  identityWords: number;
  identityBytes: number;
  descriptorEndpointBytes: number;
  hashPublicationBytes: number;
  paddingWords: number;
  totalWords: number;
  totalBytes: number;
}

export interface WideCompactContactPoolDeviceLimits {
  maxStorageBufferBindingSize: number;
  maxBufferSize: number;
}

export interface WideCompactContactPoolCapacityDiagnostic {
  layout: WideCompactContactPoolLayout;
  hashLoadFactor: number;
  largestPlane: {
    name: keyof Pick<
      WideCompactContactPoolLayout,
      | 'header'
      | 'hashPublication'
      | 'descriptorBodyA'
      | 'descriptorBodyB'
      | 'descriptorLastSeen'
      | 'descriptorResumeEpoch'
      | 'slotRow'
      | 'outputMask'
      | 'freeDescriptors'
      | 'freeRows0'
      | 'freeRows1'
    >;
    bytes: number;
  };
  allPlaneBindingsFit: boolean;
  monolithicBindingFits: boolean;
  allocationFits: boolean;
  requiresPlaneBindings: boolean;
  failures: readonly string[];
  warnings: readonly string[];
}

export type WideDescriptorBeginResult = Readonly<
  | { status: 'existing'; descriptorIndex: number; hashSlot: number; probes: number }
  | { status: 'pending'; ticket: number; descriptorIndex: number; candidateHashSlot: number; probes: number }
  | { status: 'descriptor-capacity-exceeded' | 'hash-capacity-exceeded'; probes: number }
  | { status: 'protocol-failure'; probes: number; reason: string }
>;

export type WideDescriptorPublishResult = Readonly<
  | {
    status: 'inserted' | 'existing';
    descriptorIndex: number;
    hashSlot: number;
    probes: number;
    compareExchangeFailures: number;
  }
  | {
    status: 'initialization-not-visible' | 'hash-capacity-exceeded';
    probes: number;
    compareExchangeFailures: number;
  }
  | { status: 'protocol-failure'; probes: number; compareExchangeFailures: number; reason: string }
>;

export type WideDescriptorLookupResult = Readonly<
  | { status: 'found'; descriptorIndex: number; hashSlot: number; probes: number }
  | { status: 'not-found'; probes: number }
  | { status: 'protocol-failure'; probes: number; reason: string }
>;

export interface WideDescriptorProtocolTraceEvent {
  sequence: number;
  type:
    | 'descriptor-initialized'
    | 'initialization-visible'
    | 'hash-compare-exchange'
    | 'descriptor-released';
  ticket?: number;
  descriptorIndex?: number;
  hashSlot?: number;
  publication?: number;
  exchanged?: boolean;
}

export interface WideDescriptorProtocolSnapshot {
  hashPublication: Uint32Array;
  descriptorBodyA: Uint32Array;
  descriptorBodyB: Uint32Array;
  descriptorHighWater: number;
  liveDescriptors: number;
  pendingDescriptors: number;
  reusableDescriptors: number;
  trace: readonly WideDescriptorProtocolTraceEvent[];
}

export interface WideCompactContactPoolSelfTestResult {
  ok: boolean;
  checks: number;
  failures: readonly string[];
  coliseum510k: {
    totalWords: number;
    totalBytes: number;
    hashPublicationBytes: number;
    descriptorEndpointBytes: number;
    requiresPlaneBindingsAt128MiB: boolean;
  };
}

type PlaneKey = WideCompactContactPoolCapacityDiagnostic['largestPlane']['name'];

const PLANE_KEYS = Object.freeze([
  'header',
  'hashPublication',
  'descriptorBodyA',
  'descriptorBodyB',
  'descriptorLastSeen',
  'descriptorResumeEpoch',
  'slotRow',
  'outputMask',
  'freeDescriptors',
  'freeRows0',
  'freeRows1',
] as const satisfies readonly PlaneKey[]);

function positiveSafeInteger(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new Error(`${label} must be a positive safe integer (received ${String(value)}).`);
  }
  return value;
}

function checkedProduct(label: string, ...factors: number[]): number {
  let result = 1;
  for (const factor of factors) {
    result *= factor;
    if (!Number.isSafeInteger(result)) throw new Error(`${label} exceeds exact integer range.`);
  }
  return result;
}

function checkedSum(label: string, ...terms: number[]): number {
  const result = terms.reduce((sum, term) => sum + term, 0);
  if (!Number.isSafeInteger(result)) throw new Error(`${label} exceeds exact integer range.`);
  return result;
}

function isPowerOfTwo(value: number): boolean {
  return value > 0 && Number.isInteger(Math.log2(value));
}

function alignWords(cursor: number, alignmentWords: number): number {
  return Math.ceil(cursor / alignmentWords) * alignmentWords;
}

function appendAlignedRange(
  cursor: number,
  length: number,
  alignmentWords: number,
  label: string,
): WideCompactContactPoolRange {
  const offset = alignWords(cursor, alignmentWords);
  const end = checkedSum(`${label} end`, offset, length);
  return Object.freeze({
    offset,
    length,
    end,
    byteOffset: checkedProduct(`${label} byte offset`, offset, BYTES_PER_U32),
    byteLength: checkedProduct(`${label} byte length`, length, BYTES_PER_U32),
  });
}

/** Plans the exact-wide persistent map without allocating CPU or GPU storage. */
export function planWideCompactContactPoolLayout(
  requested: WideCompactContactPoolLayoutInput,
): WideCompactContactPoolLayout {
  const hashCapacity = positiveSafeInteger(
    requested.persistentManifoldHashCapacity,
    'persistentManifoldHashCapacity',
  );
  if (!isPowerOfTwo(hashCapacity) || hashCapacity > 0x8000_0000) {
    throw new Error('persistentManifoldHashCapacity must be a power of two no larger than 2^31.');
  }
  const descriptors = positiveSafeInteger(
    requested.maxPersistentManifolds,
    'maxPersistentManifolds',
  );
  if (descriptors > WIDE_COMPACT_CONTACT_MAX_DESCRIPTOR_COUNT) {
    throw new Error('maxPersistentManifolds exceeds descriptorIndex + 1 publication capacity.');
  }
  if (descriptors >= hashCapacity) {
    throw new Error('Exact wide descriptor table requires at least one permanently empty hash slot.');
  }
  const slots = positiveSafeInteger(requested.pairManifoldSlots, 'pairManifoldSlots');
  if (slots > 8) throw new Error('pairManifoldSlots must be <= 8.');
  const rowCapacity = positiveSafeInteger(
    requested.compactContactPoolCapacity,
    'compactContactPoolCapacity',
  );
  const logicalRowLimit = checkedProduct('logical contact row limit', descriptors, slots);
  if (rowCapacity > logicalRowLimit) {
    throw new Error(
      `compactContactPoolCapacity=${rowCapacity} exceeds maxPersistentManifolds * pairManifoldSlots=${logicalRowLimit}.`,
    );
  }
  const alignmentBytes = positiveSafeInteger(
    requested.bindingOffsetAlignmentBytes ?? WIDE_COMPACT_CONTACT_DEFAULT_ALIGNMENT_BYTES,
    'bindingOffsetAlignmentBytes',
  );
  if (alignmentBytes % BYTES_PER_U32 !== 0 || !isPowerOfTwo(alignmentBytes)) {
    throw new Error('bindingOffsetAlignmentBytes must be a power of two and a multiple of four.');
  }
  const alignmentWords = alignmentBytes / BYTES_PER_U32;

  let cursor = 0;
  // Keep the established 30-word header ABI, but align every following wide
  // plane independently. The legacy planner remains contiguous and unchanged.
  const header = appendAlignedRange(cursor, 30, alignmentWords, 'header'); cursor = header.end;
  const hashPublication = appendAlignedRange(cursor, hashCapacity, alignmentWords, 'hashPublication'); cursor = hashPublication.end;
  const descriptorBodyA = appendAlignedRange(cursor, descriptors, alignmentWords, 'descriptorBodyA'); cursor = descriptorBodyA.end;
  const descriptorBodyB = appendAlignedRange(cursor, descriptors, alignmentWords, 'descriptorBodyB'); cursor = descriptorBodyB.end;
  const descriptorLastSeen = appendAlignedRange(cursor, descriptors, alignmentWords, 'descriptorLastSeen'); cursor = descriptorLastSeen.end;
  const descriptorResumeEpoch = appendAlignedRange(cursor, descriptors, alignmentWords, 'descriptorResumeEpoch'); cursor = descriptorResumeEpoch.end;
  const slotRow = appendAlignedRange(cursor, logicalRowLimit, alignmentWords, 'slotRow'); cursor = slotRow.end;
  const outputMask = appendAlignedRange(cursor, descriptors, alignmentWords, 'outputMask'); cursor = outputMask.end;
  const freeDescriptors = appendAlignedRange(cursor, descriptors, alignmentWords, 'freeDescriptors'); cursor = freeDescriptors.end;
  const freeRows0 = appendAlignedRange(cursor, rowCapacity, alignmentWords, 'freeRows0'); cursor = freeRows0.end;
  const freeRows1 = appendAlignedRange(cursor, rowCapacity, alignmentWords, 'freeRows1'); cursor = freeRows1.end;

  if (cursor > U32_MAX) {
    throw new Error(`Exact wide compact map has ${cursor} words and cannot be indexed by u32.`);
  }
  const ranges = Object.freeze([
    header,
    hashPublication,
    descriptorBodyA,
    descriptorBodyB,
    descriptorLastSeen,
    descriptorResumeEpoch,
    slotRow,
    outputMask,
    freeDescriptors,
    freeRows0,
    freeRows1,
  ]);
  const usedWords = ranges.reduce((sum, range) => checkedSum('used words', sum, range.length), 0);
  const identityWords = checkedSum('identity words', hashCapacity, descriptors, descriptors);
  return Object.freeze({
    encoding: 'exact-wide-u32',
    bindingOffsetAlignmentBytes: alignmentBytes,
    header,
    hashPublication,
    descriptorBodyA,
    descriptorBodyB,
    descriptorLastSeen,
    descriptorResumeEpoch,
    slotRow,
    outputMask,
    freeDescriptors,
    freeRows0,
    freeRows1,
    ranges,
    identityWords,
    identityBytes: checkedProduct('identity byte length', identityWords, BYTES_PER_U32),
    descriptorEndpointBytes: checkedProduct('descriptor endpoint byte length', descriptors, 2, BYTES_PER_U32),
    hashPublicationBytes: checkedProduct('hash publication byte length', hashCapacity, BYTES_PER_U32),
    paddingWords: cursor - usedWords,
    totalWords: cursor,
    totalBytes: checkedProduct('wide compact map byte length', cursor, BYTES_PER_U32),
  });
}

/** Reports the exact allocation/binding gates without touching WebGPU. */
export function diagnoseWideCompactContactPoolCapacity(
  requested: WideCompactContactPoolLayoutInput,
  limits: WideCompactContactPoolDeviceLimits,
): WideCompactContactPoolCapacityDiagnostic {
  const layout = planWideCompactContactPoolLayout(requested);
  const maxStorageBufferBindingSize = positiveSafeInteger(
    limits.maxStorageBufferBindingSize,
    'maxStorageBufferBindingSize',
  );
  const maxBufferSize = positiveSafeInteger(limits.maxBufferSize, 'maxBufferSize');
  let largestPlane: WideCompactContactPoolCapacityDiagnostic['largestPlane'] = {
    name: 'header',
    bytes: layout.header.byteLength,
  };
  for (const name of PLANE_KEYS) {
    const bytes = layout[name].byteLength;
    if (bytes > largestPlane.bytes) largestPlane = { name, bytes };
  }
  const allPlaneBindingsFit = largestPlane.bytes <= maxStorageBufferBindingSize;
  const monolithicBindingFits = layout.totalBytes <= maxStorageBufferBindingSize;
  const allocationFits = layout.totalBytes <= maxBufferSize;
  const failures: string[] = [];
  const warnings: string[] = [];
  if (!allPlaneBindingsFit) {
    failures.push(
      `${largestPlane.name} requires ${largestPlane.bytes} bytes, exceeding maxStorageBufferBindingSize=${maxStorageBufferBindingSize}.`,
    );
  }
  if (!allocationFits) {
    failures.push(
      `Wide compact allocation requires ${layout.totalBytes} bytes, exceeding maxBufferSize=${maxBufferSize}.`,
    );
  }
  if (!monolithicBindingFits && allPlaneBindingsFit) {
    warnings.push('The allocation fits only when exposed as aligned plane bindings, not one monolithic storage binding.');
  }
  const hashLoadFactor = requested.maxPersistentManifolds
    / requested.persistentManifoldHashCapacity;
  if (hashLoadFactor > 0.5) {
    warnings.push(`Descriptor hash load factor ${hashLoadFactor.toFixed(6)} exceeds the 0.5 planning target.`);
  }
  return Object.freeze({
    layout,
    hashLoadFactor,
    largestPlane: Object.freeze(largestPlane),
    allPlaneBindingsFit,
    monolithicBindingFits,
    allocationFits,
    requiresPlaneBindings: !monolithicBindingFits && allPlaneBindingsFit,
    failures: Object.freeze(failures),
    warnings: Object.freeze(warnings),
  });
}

interface DescriptorReservation {
  ticket: number;
  descriptorIndex: number;
  pair: CanonicalBodyPair;
  startSlot: number;
  candidateHashSlot: number;
  expectedPublication: number;
  visible: boolean;
}

/**
 * GPU-free staged mirror of the exact publication protocol.
 *
 * `begin()` initializes a descriptor but leaves it unreachable. A dispatch
 * boundary is represented by `makeInitializedDescriptorsVisible()`. Only then
 * may `publish()` atomically install descriptorIndex + 1 in the hash plane.
 * This two-phase rule avoids relying on relaxed cross-address atomic ordering:
 * every reader that can observe a publication necessarily observes the fully
 * initialized descriptor from the earlier dispatch.
 */
export class WidePersistentDescriptorProtocolSimulator {
  private readonly hashPublication: Uint32Array;
  private readonly descriptorBodyA: Uint32Array;
  private readonly descriptorBodyB: Uint32Array;
  private readonly descriptorState: Uint8Array;
  private readonly reservations = new Map<number, DescriptorReservation>();
  private readonly reusableDescriptors: number[] = [];
  private readonly trace: WideDescriptorProtocolTraceEvent[] = [];
  private descriptorHighWater = 0;
  private nextTicket = 1;
  private sequence = 0;

  constructor(
    readonly hashCapacity: number,
    readonly descriptorCapacity: number,
  ) {
    positiveSafeInteger(hashCapacity, 'hashCapacity');
    positiveSafeInteger(descriptorCapacity, 'descriptorCapacity');
    if (!isPowerOfTwo(hashCapacity)) throw new Error('hashCapacity must be a power of two.');
    if (descriptorCapacity > WIDE_COMPACT_CONTACT_MAX_DESCRIPTOR_COUNT) {
      throw new Error('descriptorCapacity exceeds descriptorIndex + 1 publication capacity.');
    }
    this.hashPublication = new Uint32Array(hashCapacity);
    this.descriptorBodyA = new Uint32Array(descriptorCapacity);
    this.descriptorBodyB = new Uint32Array(descriptorCapacity);
    this.descriptorState = new Uint8Array(descriptorCapacity);
  }

  begin(bodyI: number, bodyJ: number): WideDescriptorBeginResult {
    const pair = canonicalBodyPair(bodyI, bodyJ);
    const probe = this.probe(pair);
    if (probe.status === 'found') {
      return Object.freeze({
        status: 'existing',
        descriptorIndex: probe.descriptorIndex,
        hashSlot: probe.hashSlot,
        probes: probe.probes,
      });
    }
    if (probe.status === 'protocol-failure') return probe;
    if (probe.insertionSlot === undefined) {
      return Object.freeze({ status: 'hash-capacity-exceeded', probes: probe.probes });
    }
    const descriptorIndex = this.allocateDescriptor();
    if (descriptorIndex === undefined) {
      return Object.freeze({ status: 'descriptor-capacity-exceeded', probes: probe.probes });
    }
    this.descriptorBodyA[descriptorIndex] = pair.bodyA;
    this.descriptorBodyB[descriptorIndex] = pair.bodyB;
    this.descriptorState[descriptorIndex] = 1; // initialized, not dispatch-visible
    const ticket = this.nextTicket++;
    const reservation: DescriptorReservation = {
      ticket,
      descriptorIndex,
      pair,
      startSlot: hashCanonicalBodyPair(pair) & (this.hashCapacity - 1),
      candidateHashSlot: probe.insertionSlot,
      expectedPublication: probe.expectedPublication!,
      visible: false,
    };
    this.reservations.set(ticket, reservation);
    this.record({ type: 'descriptor-initialized', ticket, descriptorIndex });
    return Object.freeze({
      status: 'pending',
      ticket,
      descriptorIndex,
      candidateHashSlot: probe.insertionSlot,
      probes: probe.probes,
    });
  }

  /** CPU mirror of the storage-visibility guarantee at a dispatch boundary. */
  makeInitializedDescriptorsVisible(): number {
    let count = 0;
    for (const reservation of this.reservations.values()) {
      if (reservation.visible) continue;
      reservation.visible = true;
      this.descriptorState[reservation.descriptorIndex] = 2;
      this.record({
        type: 'initialization-visible',
        ticket: reservation.ticket,
        descriptorIndex: reservation.descriptorIndex,
      });
      count++;
    }
    return count;
  }

  publish(ticket: number): WideDescriptorPublishResult {
    const reservation = this.reservations.get(ticket);
    if (!reservation) {
      return Object.freeze({
        status: 'protocol-failure',
        probes: 0,
        compareExchangeFailures: 0,
        reason: `Unknown or already completed publication ticket ${String(ticket)}.`,
      });
    }
    if (!reservation.visible || this.descriptorState[reservation.descriptorIndex] !== 2) {
      return Object.freeze({
        status: 'initialization-not-visible',
        probes: 0,
        compareExchangeFailures: 0,
      });
    }

    const publication = encodeWidePairDescriptorPublication(reservation.descriptorIndex);
    let compareExchangeFailures = 0;
    // First mirror the CAS against the slot observed during phase one. This
    // makes two staged contenders exercise the real stale-observation path.
    if (this.compareExchange(
      reservation.candidateHashSlot,
      reservation.expectedPublication,
      publication,
      reservation,
    )) {
      return this.finishPublished(reservation, reservation.candidateHashSlot, 1, 0);
    }
    compareExchangeFailures++;

    let firstTombstone: number | undefined;
    for (let probeIndex = 0; probeIndex < this.hashCapacity; probeIndex++) {
      const slot = (reservation.startSlot + probeIndex) & (this.hashCapacity - 1);
      const existingPublication = this.hashPublication[slot];
      if (existingPublication === WIDE_PAIR_HASH_TOMBSTONE) {
        firstTombstone ??= slot;
        continue;
      }
      if (existingPublication === WIDE_PAIR_HASH_EMPTY) {
        const insertionSlot = firstTombstone ?? slot;
        const expected = firstTombstone === undefined
          ? WIDE_PAIR_HASH_EMPTY
          : WIDE_PAIR_HASH_TOMBSTONE;
        if (this.compareExchange(insertionSlot, expected, publication, reservation)) {
          return this.finishPublished(
            reservation,
            insertionSlot,
            probeIndex + 1,
            compareExchangeFailures,
          );
        }
        compareExchangeFailures++;
        probeIndex = -1;
        firstTombstone = undefined;
        continue;
      }
      const descriptorIndex = decodeWidePairDescriptorPublication(existingPublication);
      if (descriptorIndex === null || descriptorIndex >= this.descriptorCapacity) {
        return Object.freeze({
          status: 'protocol-failure',
          probes: probeIndex + 1,
          compareExchangeFailures,
          reason: `Hash slot ${slot} published invalid descriptor ${String(existingPublication)}.`,
        });
      }
      if (this.descriptorState[descriptorIndex] !== 3) {
        return Object.freeze({
          status: 'protocol-failure',
          probes: probeIndex + 1,
          compareExchangeFailures,
          reason: `Hash slot ${slot} made descriptor ${descriptorIndex} visible before initialization.`,
        });
      }
      if (this.descriptorEquals(descriptorIndex, reservation.pair)) {
        this.releaseReservation(reservation);
        return Object.freeze({
          status: 'existing',
          descriptorIndex,
          hashSlot: slot,
          probes: probeIndex + 1,
          compareExchangeFailures,
        });
      }
    }

    this.releaseReservation(reservation);
    return Object.freeze({
      status: 'hash-capacity-exceeded',
      probes: this.hashCapacity,
      compareExchangeFailures,
    });
  }

  lookup(bodyI: number, bodyJ: number): WideDescriptorLookupResult {
    const result = this.probe(canonicalBodyPair(bodyI, bodyJ));
    if (result.status === 'found' || result.status === 'protocol-failure') return result;
    return Object.freeze({ status: 'not-found', probes: result.probes });
  }

  resolve(bodyI: number, bodyJ: number): WideDescriptorPublishResult | WideDescriptorBeginResult {
    const begin = this.begin(bodyI, bodyJ);
    if (begin.status !== 'pending') return begin;
    this.makeInitializedDescriptorsVisible();
    return this.publish(begin.ticket);
  }

  snapshot(): WideDescriptorProtocolSnapshot {
    let liveDescriptors = 0;
    let pendingDescriptors = 0;
    for (const state of this.descriptorState) {
      if (state === 3) liveDescriptors++;
      else if (state === 1 || state === 2) pendingDescriptors++;
    }
    return Object.freeze({
      hashPublication: this.hashPublication.slice(),
      descriptorBodyA: this.descriptorBodyA.slice(),
      descriptorBodyB: this.descriptorBodyB.slice(),
      descriptorHighWater: this.descriptorHighWater,
      liveDescriptors,
      pendingDescriptors,
      reusableDescriptors: this.reusableDescriptors.length,
      trace: Object.freeze(this.trace.map((event) => Object.freeze({ ...event }))),
    });
  }

  private probe(pair: CanonicalBodyPair): (
    | { status: 'found'; descriptorIndex: number; hashSlot: number; probes: number }
    | { status: 'missing'; probes: number; insertionSlot?: number; expectedPublication?: number }
    | { status: 'protocol-failure'; probes: number; reason: string }
  ) {
    const mask = this.hashCapacity - 1;
    const startSlot = hashCanonicalBodyPair(pair) & mask;
    let firstTombstone: number | undefined;
    for (let probeIndex = 0; probeIndex < this.hashCapacity; probeIndex++) {
      const slot = (startSlot + probeIndex) & mask;
      const publication = this.hashPublication[slot];
      if (publication === WIDE_PAIR_HASH_TOMBSTONE) {
        firstTombstone ??= slot;
        continue;
      }
      if (publication === WIDE_PAIR_HASH_EMPTY) {
        return {
          status: 'missing',
          probes: probeIndex + 1,
          insertionSlot: firstTombstone ?? slot,
          expectedPublication: firstTombstone === undefined
            ? WIDE_PAIR_HASH_EMPTY
            : WIDE_PAIR_HASH_TOMBSTONE,
        };
      }
      const descriptorIndex = decodeWidePairDescriptorPublication(publication);
      if (descriptorIndex === null || descriptorIndex >= this.descriptorCapacity) {
        return {
          status: 'protocol-failure',
          probes: probeIndex + 1,
          reason: `Hash slot ${slot} published invalid descriptor ${String(publication)}.`,
        };
      }
      if (this.descriptorState[descriptorIndex] !== 3) {
        return {
          status: 'protocol-failure',
          probes: probeIndex + 1,
          reason: `Hash slot ${slot} exposed descriptor ${descriptorIndex} before initialization.`,
        };
      }
      if (this.descriptorEquals(descriptorIndex, pair)) {
        return { status: 'found', descriptorIndex, hashSlot: slot, probes: probeIndex + 1 };
      }
    }
    if (firstTombstone !== undefined) {
      return {
        status: 'missing',
        probes: this.hashCapacity,
        insertionSlot: firstTombstone,
        expectedPublication: WIDE_PAIR_HASH_TOMBSTONE,
      };
    }
    return { status: 'missing', probes: this.hashCapacity };
  }

  private allocateDescriptor(): number | undefined {
    const reused = this.reusableDescriptors.pop();
    if (reused !== undefined) return reused;
    if (this.descriptorHighWater >= this.descriptorCapacity) return undefined;
    return this.descriptorHighWater++;
  }

  private descriptorEquals(descriptorIndex: number, pair: CanonicalBodyPair): boolean {
    return this.descriptorBodyA[descriptorIndex] === pair.bodyA
      && this.descriptorBodyB[descriptorIndex] === pair.bodyB;
  }

  private compareExchange(
    hashSlot: number,
    expected: number,
    publication: number,
    reservation: DescriptorReservation,
  ): boolean {
    const exchanged = this.hashPublication[hashSlot] === expected;
    if (exchanged) this.hashPublication[hashSlot] = publication;
    this.record({
      type: 'hash-compare-exchange',
      ticket: reservation.ticket,
      descriptorIndex: reservation.descriptorIndex,
      hashSlot,
      publication,
      exchanged,
    });
    return exchanged;
  }

  private finishPublished(
    reservation: DescriptorReservation,
    hashSlot: number,
    probes: number,
    compareExchangeFailures: number,
  ): WideDescriptorPublishResult {
    this.descriptorState[reservation.descriptorIndex] = 3;
    this.reservations.delete(reservation.ticket);
    return Object.freeze({
      status: 'inserted',
      descriptorIndex: reservation.descriptorIndex,
      hashSlot,
      probes,
      compareExchangeFailures,
    });
  }

  private releaseReservation(reservation: DescriptorReservation): void {
    this.reservations.delete(reservation.ticket);
    this.descriptorState[reservation.descriptorIndex] = 0;
    this.descriptorBodyA[reservation.descriptorIndex] = 0;
    this.descriptorBodyB[reservation.descriptorIndex] = 0;
    this.reusableDescriptors.push(reservation.descriptorIndex);
    this.record({
      type: 'descriptor-released',
      ticket: reservation.ticket,
      descriptorIndex: reservation.descriptorIndex,
    });
  }

  private record(event: Omit<WideDescriptorProtocolTraceEvent, 'sequence'>): void {
    this.trace.push({ sequence: this.sequence++, ...event });
  }
}

/** GPU-free layout, capacity, collision, and staged-publication checks. */
export function runWideCompactContactPoolSelfTests(): WideCompactContactPoolSelfTestResult {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };

  const coliseumInput = {
    persistentManifoldHashCapacity: 4_194_304,
    maxPersistentManifolds: 2_097_152,
    pairManifoldSlots: 8,
    compactContactPoolCapacity: 7_340_032,
  } as const;
  const diagnostic = diagnoseWideCompactContactPoolCapacity(coliseumInput, {
    maxStorageBufferBindingSize: 128 * 1024 * 1024,
    maxBufferSize: 256 * 1024 * 1024,
  });
  const layout = diagnostic.layout;
  check('510K hash publication plane is exactly 16 MiB',
    layout.hashPublicationBytes === 16 * 1024 * 1024);
  check('510K descriptor endpoint planes are exactly 16 MiB',
    layout.descriptorEndpointBytes === 16 * 1024 * 1024);
  check('510K exact-wide compact map has literal aligned size',
    layout.totalWords === 48_234_560 && layout.totalBytes === 192_938_240);
  check('510K individual compact planes fit the 128 MiB binding limit',
    diagnostic.allPlaneBindingsFit && diagnostic.largestPlane.name === 'slotRow');
  check('510K compact map diagnoses required plane bindings',
    diagnostic.requiresPlaneBindings && !diagnostic.monolithicBindingFits && diagnostic.allocationFits);
  check('every wide compact plane begins at the requested binding alignment',
    PLANE_KEYS.every((key) => layout[key].byteOffset % layout.bindingOffsetAlignmentBytes === 0));

  // These two distinct endpoint pairs are a known full 32-bit collision in
  // hashCanonicalBodyPair(), not merely a collision after applying a tiny mask.
  const collisionLeft = canonicalBodyPair(65, 686);
  const collisionRight = canonicalBodyPair(228, 427);
  check('known exact-pair full hash collision remains reproducible',
    hashCanonicalBodyPair(collisionLeft) === hashCanonicalBodyPair(collisionRight)
      && !equalCanonicalBodyPair(collisionLeft, collisionRight));

  const collisionProtocol = new WidePersistentDescriptorProtocolSimulator(8, 8);
  const leftPending = collisionProtocol.begin(collisionLeft.bodyA, collisionLeft.bodyB);
  const rightPending = collisionProtocol.begin(collisionRight.bodyA, collisionRight.bodyB);
  check('concurrent colliders reserve initialized but unpublished descriptors',
    leftPending.status === 'pending'
      && rightPending.status === 'pending'
      && leftPending.candidateHashSlot === rightPending.candidateHashSlot
      && collisionProtocol.lookup(collisionLeft.bodyA, collisionLeft.bodyB).status === 'not-found'
      && collisionProtocol.lookup(collisionRight.bodyA, collisionRight.bodyB).status === 'not-found');
  const premature = leftPending.status === 'pending'
    ? collisionProtocol.publish(leftPending.ticket)
    : undefined;
  check('publication is rejected before descriptor initialization is dispatch-visible',
    premature?.status === 'initialization-not-visible');
  collisionProtocol.makeInitializedDescriptorsVisible();
  const leftPublished = leftPending.status === 'pending'
    ? collisionProtocol.publish(leftPending.ticket)
    : undefined;
  const rightPublished = rightPending.status === 'pending'
    ? collisionProtocol.publish(rightPending.ticket)
    : undefined;
  check('different endpoint pairs sharing one full hash publish distinct descriptors',
    leftPublished?.status === 'inserted'
      && rightPublished?.status === 'inserted'
      && leftPublished.descriptorIndex !== rightPublished.descriptorIndex
      && leftPublished.hashSlot !== rightPublished.hashSlot
      && rightPublished.compareExchangeFailures === 1);
  const leftLookup = collisionProtocol.lookup(collisionLeft.bodyA, collisionLeft.bodyB);
  const rightLookup = collisionProtocol.lookup(collisionRight.bodyA, collisionRight.bodyB);
  check('hash collision lookup decides identity only by both descriptor endpoints',
    leftLookup.status === 'found'
      && rightLookup.status === 'found'
      && leftPublished?.status === 'inserted'
      && rightPublished?.status === 'inserted'
      && leftLookup.descriptorIndex === leftPublished.descriptorIndex
      && rightLookup.descriptorIndex === rightPublished.descriptorIndex);
  const collisionSnapshot = collisionProtocol.snapshot();
  const firstPublicationEvent = collisionSnapshot.trace.find(
    (event) => event.type === 'hash-compare-exchange' && event.exchanged,
  );
  const lastVisibilitySequence = collisionSnapshot.trace
    .filter((event) => event.type === 'initialization-visible')
    .reduce((latest, event) => Math.max(latest, event.sequence), -1);
  check('all descriptor initialization becomes visible before the first hash publication',
    firstPublicationEvent !== undefined && lastVisibilitySequence < firstPublicationEvent.sequence);

  const duplicateProtocol = new WidePersistentDescriptorProtocolSimulator(8, 3);
  const duplicateA = duplicateProtocol.begin(65_536, 509_999);
  const duplicateB = duplicateProtocol.begin(509_999, 65_536);
  duplicateProtocol.makeInitializedDescriptorsVisible();
  const duplicateAPublished = duplicateA.status === 'pending'
    ? duplicateProtocol.publish(duplicateA.ticket)
    : undefined;
  const duplicateBPublished = duplicateB.status === 'pending'
    ? duplicateProtocol.publish(duplicateB.ticket)
    : undefined;
  const duplicateSnapshot = duplicateProtocol.snapshot();
  check('same-pair publication race converges to one exact descriptor',
    duplicateAPublished?.status === 'inserted'
      && duplicateBPublished?.status === 'existing'
      && duplicateBPublished.descriptorIndex === duplicateAPublished.descriptorIndex
      && duplicateSnapshot.liveDescriptors === 1
      && duplicateSnapshot.pendingDescriptors === 0
      && duplicateSnapshot.reusableDescriptors === 1);
  const reused = duplicateProtocol.resolve(70_000, 135_536);
  check('losing publication reservation returns its descriptor without leaking capacity',
    reused.status === 'inserted' && reused.descriptorIndex === 1);

  let rejectedDenseHash = false;
  try {
    planWideCompactContactPoolLayout({
      ...coliseumInput,
      persistentManifoldHashCapacity: coliseumInput.maxPersistentManifolds,
    });
  } catch { rejectedDenseHash = true; }
  check('wide compact planner reserves at least one empty hash slot', rejectedDenseHash);
  let rejectedUnaligned = false;
  try {
    planWideCompactContactPoolLayout({ ...coliseumInput, bindingOffsetAlignmentBytes: 12 });
  } catch { rejectedUnaligned = true; }
  check('wide compact planner rejects invalid plane binding alignment', rejectedUnaligned);

  return Object.freeze({
    ok: failures.length === 0,
    checks,
    failures: Object.freeze(failures),
    coliseum510k: Object.freeze({
      totalWords: layout.totalWords,
      totalBytes: layout.totalBytes,
      hashPublicationBytes: layout.hashPublicationBytes,
      descriptorEndpointBytes: layout.descriptorEndpointBytes,
      requiresPlaneBindingsAt128MiB: diagnostic.requiresPlaneBindings,
    }),
  });
}
